import { useCallback, useEffect, useRef, useState } from 'react';
import { AutosaveStatus } from '@components/AutosaveStatus';
import { useAutoSave } from '@hooks/useAutoSave';
import { Box, Stack, Switch, TableCell, TableRow, TextField, Typography } from '@mui/material';

import { MAX_LABEL_LENGTH, requirementKindLabel } from '../constants';
import type { DefinitionResponse, SubjectLanguage } from '../types';

export interface RequirementDefinitionRowProps {
  definition: DefinitionResponse;
  /** Rejects to let the row surface the failure and roll its control back. */
  onUpdate: (patch: {
    label?: string;
    active?: boolean;
    default_for_new_patients?: boolean;
  }) => Promise<void>;
  subject: SubjectLanguage;
  disabled?: boolean;
}

/**
 * One catalog row: the patient-facing label (AC-1, autosaved) plus the active
 * and new-patient-default switches (PRD §3 — labels, active state, new-patient
 * defaults).
 */
export const RequirementDefinitionRow = ({
  definition,
  onUpdate,
  subject,
  disabled = false,
}: RequirementDefinitionRowProps) => {
  const [label, setLabel] = useState(definition.label);
  const pendingLabelRef = useRef<string | null>(null);

  // Adopt server-side label changes (another admin, another tab) unless this
  // row has an unsaved edit that would be clobbered by doing so.
  useEffect(() => {
    if (pendingLabelRef.current === null) {
      setLabel(definition.label);
    }
  }, [definition.label]);

  const saveLabel = useCallback(
    async (next: string) => {
      await onUpdate({ label: next });
      // Clear the pending marker only if it still refers to the value that
      // just saved. An older request settling after the admin has typed again
      // would otherwise mark the newer text as saved, and the effect below
      // would then adopt the older server label over what is on screen.
      if (pendingLabelRef.current === next) {
        pendingLabelRef.current = null;
      }
    },
    [onUpdate],
  );

  const { triggerAutoSave, saveImmediately, cancelPendingSave, status, lastSavedAt } =
    useAutoSave<string>({
      onSave: saveLabel,
      debounceMs: 2000,
      flushOnUnmount: true,
    });

  const handleLabelChange = (next: string) => {
    setLabel(next);
    pendingLabelRef.current = next;
    triggerAutoSave(next);
  };

  const handleLabelBlur = () => {
    const pending = pendingLabelRef.current;
    if (pending === null) return;

    // Discarding an edit must cancel the debounce that typing already armed.
    // Restoring only the visible field would leave the timer running, and it
    // fires ~2s later with the value we just discarded — overwriting the label
    // the row has just put back on screen.
    if (pending.trim() === '' || pending === definition.label) {
      cancelPendingSave();
      // An empty label would leave the patient page with an unnamed item, and
      // the API rejects it, so restore the saved value rather than save blank.
      setLabel(definition.label);
      pendingLabelRef.current = null;
      return;
    }

    saveImmediately(pending);
  };

  const kindName = requirementKindLabel(definition.kind);

  return (
    <TableRow>
      <TableCell sx={{ verticalAlign: 'top', width: '32%' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {kindName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {definition.kind === 'payment_method'
            ? `Built in. Satisfied when the ${subject.singular} has a saved card.`
            : `Satisfied when the ${subject.singular} provides this item.`}
        </Typography>
      </TableCell>

      <TableCell sx={{ verticalAlign: 'top' }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
          <TextField
            size="small"
            fullWidth
            value={label}
            onChange={event => handleLabelChange(event.target.value)}
            onBlur={handleLabelBlur}
            disabled={disabled}
            label={`${subject.singularCap}-facing label`}
            slotProps={{ htmlInput: { maxLength: MAX_LABEL_LENGTH } }}
            helperText={`${subject.pluralCap} see this on the update page. ${kindName} is the internal name.`}
          />
          <Box sx={{ pt: 1 }}>
            <AutosaveStatus
              status={status}
              isDirty={pendingLabelRef.current !== null}
              lastSavedAt={lastSavedAt}
            />
          </Box>
        </Stack>
      </TableCell>

      <TableCell align="center" sx={{ verticalAlign: 'top', width: '14%' }}>
        <Switch
          checked={definition.active}
          disabled={disabled}
          onChange={event => {
            void onUpdate({ active: event.target.checked });
          }}
          slotProps={{ input: { 'aria-label': `${kindName} active` } }}
        />
      </TableCell>

      <TableCell align="center" sx={{ verticalAlign: 'top', width: '18%' }}>
        <Switch
          checked={definition.default_for_new_patients}
          disabled={disabled}
          onChange={event => {
            void onUpdate({ default_for_new_patients: event.target.checked });
          }}
          slotProps={{
            input: { 'aria-label': `${kindName} assigned to new ${subject.plural}` },
          }}
        />
      </TableCell>
    </TableRow>
  );
};
